import { useWindowResize } from '@helpers/hooks/use-window-resize/useWindowResize';
import clsx from 'clsx';
import * as d3 from 'd3';
import { useEffect, useMemo, useRef, useState } from 'react';

import { CHART_THRESHOLD_FILL_CANVAS_ATTR } from '../constants/chart-constants';
import { TsPoint } from '../multi-line-chart/MultiLineChart.types';
import { drawThresholdFill } from '../multi-line-chart/utils';
import { PriceChangesChartAxes } from './components/price-changes-chart-axes/PriceChangesChartAxes';
import { PriceChangesChartOverlay } from './components/price-changes-chart-overlay/PriceChangesChartOverlay';
import { PriceChangesChartTooltip } from './components/price-changes-chart-tooltip/PriceChangesChartTooltip';
import { PriceChangesChartLinePlot } from './components/price-changes-line-chart-line-plot/PriceChangesChartLinePlot';
import styles from './PriceChangesChart.module.scss';
import {
  PriceChangesChartLine,
  PriceChangesTooltipData,
  PriceChangesTooltipSort,
} from './PriceChangesChart.types';

const PriceChangesChartFormatters = {
  percent1f: (v: number) => `${d3.format('.1f')(v)}%`,
  percent2f: (v: number) => `${d3.format('.2f')(v)}%`,
  percent: (v: number) => `${d3.format('.0f')(v)}%`,
  default: (v: number) => v.toString(),
  default2f: (v: number) => d3.format('.2f')(v),
};

const THRESHOLD_LABEL_WIDTH = 93;
const THRESHOLD_LABEL_PADDING = 12;

export type PriceChangesChartProps = {
  lines: PriceChangesChartLine[];
  hasTooltip?: boolean;
  tooltipFormatType?: keyof typeof PriceChangesChartFormatters;
  tooltipSort?: PriceChangesTooltipSort;
  yAxisFormatType?: keyof typeof PriceChangesChartFormatters;
  fillColors?: {
    positive: string;
    negative: string;
    neutral?: string;
  };
  locale?: string;
  useTimeScale?: boolean;
  verticalLines?: { x: Date; isDashed?: boolean }[];
  prevClosePrice?: number;
  threshold?: number;
  thresholdLabel?: string;
  toDateEST?: Date;
  fromDateEST?: Date;
  /** Label the x-axis with calendar dates. Use for daily-resolution series. */
  useCalendarDateAxis?: boolean;
};

export const PriceChangesChart = ({
  lines,
  hasTooltip = false,
  tooltipFormatType = 'default',
  tooltipSort,
  yAxisFormatType = 'default',
  fillColors,
  locale,
  useTimeScale = false,
  verticalLines = [],
  prevClosePrice,
  threshold,
  thresholdLabel,
  toDateEST,
  fromDateEST,
  useCalendarDateAxis = false,
}: PriceChangesChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { width: w, height: h } = useWindowResize({ targetRef: containerRef });
  const [tooltipData, setTooltipData] = useState<PriceChangesTooltipData>();

  const MARGIN = { top: 10, right: 50, bottom: 30, left: 20 };
  const chartWidth = w - MARGIN.right - MARGIN.left;
  const chartHeight = h - MARGIN.top - MARGIN.bottom;

  const thresholdData = useMemo(() => {
    return lines.length === 1
      ? lines[0].data.filter(
          (item): item is TsPoint => item.value !== undefined
        )
      : undefined;
  }, [lines]);

  const mergedData = useMemo(
    () =>
      lines
        .flatMap(line => line.data)
        .sort((a, b) => a.date.getTime() - b.date.getTime()),
    [lines]
  );

  const uniqueDates = useMemo(
    () =>
      Array.from(new Set(mergedData.map(point => point.date.getTime()))).map(
        timestamp => new Date(timestamp)
      ),
    [mergedData]
  );

  const [minY = 0, maxY = 0] = useMemo(() => {
    const values = mergedData
      .map(p => p.value)
      .filter((v): v is number => v !== undefined);

    if (threshold !== undefined) {
      values.push(threshold);
    }

    return d3.extent(values);
  }, [mergedData, threshold]);

  const [minX = new Date(), maxX = new Date()] = useMemo(
    () => d3.extent(mergedData.map(p => p.date)),
    [mergedData]
  );

  const maxXDate = toDateEST ? toDateEST : maxX;
  const minXDate = fromDateEST ? fromDateEST : minX;

  // Create a map from date timestamp to index for gap removal
  const dateToIndexMap = useMemo(() => {
    if (useTimeScale) {
      return undefined;
    }

    const map = new Map<number, number>();
    uniqueDates.forEach((date, index) => {
      map.set(date.getTime(), index);
    });
    return map;
  }, [uniqueDates, useTimeScale]);

  const xScale = useMemo(() => {
    if (useTimeScale) {
      return d3
        .scaleLinear()
        .domain([minXDate.getTime(), maxXDate.getTime()])
        .range([0, chartWidth]);
    }

    const domainEnd = uniqueDates.length - 1;
    return d3.scaleLinear().domain([0, domainEnd]).range([0, chartWidth]);
  }, [minXDate, maxXDate, chartWidth, uniqueDates, useTimeScale]);

  const yRange = maxY - minY;
  const yPadding =
    yRange > 0
      ? 0.1 * yRange
      : 0.05 * Math.max(Math.abs(maxY), Math.abs(minY), 1);
  const yScale = useMemo(() => {
    return d3
      .scaleLinear()
      .domain([minY - yPadding, maxY + yPadding])
      .range([chartHeight, 0]);
  }, [minY, maxY, h]);

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    if (threshold === undefined || !thresholdData?.length || !fillColors) {
      ctx.clearRect(0, 0, chartWidth, chartHeight);
      return;
    }

    const hoveredValue = tooltipData
      ? lines[0]?.data.find(
          d => d.date.valueOf() === tooltipData.date.valueOf()
        )?.value
      : undefined;
    const lastValue = lines[0]?.data.findLast(
      d => d.value !== undefined
    )?.value;
    const activeValue = hoveredValue ?? lastValue;
    const isPositive = activeValue !== undefined && activeValue >= threshold;
    const fillColor = isPositive ? fillColors.positive : fillColors.negative;

    drawThresholdFill(
      ctx,
      thresholdData,
      xScale,
      yScale,
      chartWidth,
      chartHeight,
      threshold,
      { ...fillColors, positive: fillColor, negative: fillColor },
      dateToIndexMap,
      tooltipData?.xPos
    );
  }, [
    threshold,
    thresholdData,
    xScale,
    yScale,
    chartWidth,
    chartHeight,
    fillColors,
    dateToIndexMap,
    tooltipData?.xPos,
  ]);

  return (
    <div className={styles.priceChangesChartRoot} data-clipboard-snapshot>
      {tooltipData && (
        <PriceChangesChartTooltip
          lines={lines}
          tooltipData={{
            ...tooltipData,
            xPos: tooltipData.xPos + MARGIN.left,
          }}
          formatter={PriceChangesChartFormatters[tooltipFormatType]}
          sortBy={tooltipSort}
          prevClosePrice={prevClosePrice}
          locale={locale}
          useCalendarDateAxis={useCalendarDateAxis}
        />
      )}
      <div className={styles.priceChangesChartContainer} ref={containerRef}>
        {!!w && !!h && (
          <canvas
            ref={canvasRef}
            className={styles.canvasOverlay}
            width={chartWidth}
            height={chartHeight}
            style={{ top: MARGIN.top, left: MARGIN.left }}
            {...{ [CHART_THRESHOLD_FILL_CANVAS_ATTR]: true }}
          />
        )}
        <svg className={styles.plot} data-testid="price-changes-chart">
          <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
            <PriceChangesChartAxes
              xScale={xScale}
              yScale={yScale}
              yAxisFormatter={PriceChangesChartFormatters[yAxisFormatType]}
              locale={locale}
              dateToIndexMap={dateToIndexMap}
              useCalendarDateAxis={useCalendarDateAxis}
            />
            {tooltipData && (
              <line
                className={styles.tooltipLine}
                data-testid="price-changes-chart-tooltip-line"
                x1={tooltipData.xPos}
                x2={tooltipData.xPos}
                y1={0}
                y2={chartHeight}
              />
            )}
            {!!w && !!h && (
              <>
                {lines.map((line, index) => (
                  <PriceChangesChartLinePlot
                    key={line.label || index}
                    color={line.color}
                    xScale={xScale}
                    yScale={yScale}
                    data={line.data}
                    dashed={line.isBenchmark}
                    tooltipDate={tooltipData?.date}
                    dateToIndexMap={dateToIndexMap}
                    threshold={threshold}
                    tooltipXPos={tooltipData?.xPos}
                  />
                ))}
                {!!thresholdData?.length && threshold !== undefined && (
                  <>
                    <line
                      className={styles.thresholdLine}
                      x1={0}
                      x2={chartWidth}
                      y1={yScale(threshold)}
                      y2={yScale(threshold)}
                    />
                    {thresholdLabel && (
                      <foreignObject
                        x={
                          chartWidth -
                          THRESHOLD_LABEL_WIDTH -
                          THRESHOLD_LABEL_PADDING
                        }
                        y={yScale(threshold) - 20}
                        width={100}
                        className={styles.thresholdLabelForeignObject}
                      >
                        <div className={styles.thresholdLabelBlock}>
                          <span className={styles.thresholdLabel}>
                            {thresholdLabel}
                          </span>
                          <span className={styles.thresholdLabelPrice}>
                            {PriceChangesChartFormatters[yAxisFormatType](
                              threshold
                            )}
                          </span>
                        </div>
                      </foreignObject>
                    )}
                  </>
                )}
                {verticalLines.map(line => {
                  const posX = dateToIndexMap
                    ? xScale(dateToIndexMap.get(line.x.getTime()) ?? 0)
                    : xScale(line.x.getTime());
                  const posY1 = yScale(yScale.domain()[0]);
                  const posY2 = yScale(yScale.domain()[1]);
                  return (
                    <g key={line.x.toISOString()}>
                      <line
                        className={clsx(styles.verticalLine, {
                          [styles.dashed]: line.isDashed,
                        })}
                        x1={posX}
                        y1={posY1}
                        x2={posX}
                        y2={posY2}
                      />
                    </g>
                  );
                })}
                {hasTooltip && (
                  <PriceChangesChartOverlay
                    width={chartWidth}
                    height={chartHeight}
                    xScale={xScale}
                    uniqueDates={uniqueDates}
                    dateToIndexMap={dateToIndexMap}
                    setTooltipData={setTooltipData}
                  />
                )}
              </>
            )}
          </g>
        </svg>
      </div>
    </div>
  );
};
